import type { ValidEndpoints } from '@/api/clients/backend-client'
import { buildFetchHeaders, buildUrl, fetchCollection } from './helper-functions'

export type Invite_DTO = {
  id: string
  email: string
  token: string
  fleet_id: string
  expires_at: Date
  created_at: Date
  created_by: string
}

/** Request type for creating an invite. */
export type Create_Invite_Req = {
  email: string
}

/** Request type for accepting an invite. */
export type Accept_Invite_Req = {
  token: string
}

// Accept invite responses is a 204 no content on success, and an error object on failure. We can not type the 204 response, but we can type the error response for better error handling.

/** The error message for invalid or expired invite tokens. */
export const INVITE_INVALID_ERROR_MESSAGE = 'Invalid or expired invite token'

/** Type for PostgREST error responses. */
type PostgREST_error = {
  code: string
  details: string | null
  hint: string | null
  message: string
}

/**
 * Checks if the error body corresponds to an invalid invite error, which is
 * returned by the backend when a user tries to accept an invite with an invalid
 * or expired token. This allows us to provide a more specific error message to
 * the user in this case.
 *
 * @param errorBody the body of the error response returned by the backend when
 * trying to accept an invite. This is expected to be a PostgREST error object,
 * but we type it as unknown here since we need to check if it is indeed a
 * PostgREST error and if it contains the specific message indicating an invalid
 * invite.
 *
 * @returns `true` if the error body is an object containing a `message` field
 * that matches the backend's invalid or expired invite error message, otherwise
 * `false`. When `true`, TypeScript will narrow `errorBody` to `PostgREST_error`.
 */
function isInviteInvalidError(errorBody: unknown): errorBody is PostgREST_error {
  return (
    typeof errorBody === 'object' &&
    errorBody !== null &&
    'message' in errorBody &&
    typeof (errorBody as any).message === 'string' &&
    (errorBody as any).message.includes(INVITE_INVALID_ERROR_MESSAGE)
  )
}

/**
 * Creates a new invite for a user with the given email.
 *
 * @param email the email address of the user to invite. This should be the email
 * address associated with the user's account, and is used by the backend to
 * create the invite and send the invite email.
 *
 * @returns a promise that resolves to the created invite object if the invite was
 * successfully created, or rejects with an error if the invite could not be
 * created (e.g. if the user is already in a fleet, or if the user's token is
 * invalid/expired).
 *
 * @throws will throw an error if the invite could not be created, which could
 * happen if the user is already assigned to a fleet, or if the user's token is
 * invalid/expired. The error message will provide more details about the failure.
 */
export function createInvite(email: string): Promise<Invite_DTO> {
  const endpoint: ValidEndpoints = 'rpc/create_invite'

  return fetch(buildUrl(endpoint), {
    method: 'POST',
    headers: buildFetchHeaders(),
    body: JSON.stringify({ invitee_email: email }),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to create invite: ${response.status} ${response.statusText}`)
    }
    return response.json() as Promise<Invite_DTO>
  })
}

/**
 * Accepts an invite with the given token.
 *
 * @param token the invite token that was sent to the user's email when the invite 
 * was created. This token is used by the backend to identify the invite and the 
 * user accepting it, and to add the user to the fleet if the token is valid.
 * 
 * @returns a promise that resolves if the invite is accepted successfully, or 
 * rejects with an error if the invite could not be accepted.
 * 
 * @throws will throw an error if the invite could not be accepted, which could
 * happen if the token is invalid or expired, if the user is already assigned to a
 * fleet, or if the user's token is invalid/expired. The error message will provide
 * more details about the failure, and in the case of an invalid or expired invite
 * token, it will specifically indicate that the token is invalid or expired.
 */
export function acceptInvite(token: string): Promise<void> {
  const endpoint: ValidEndpoints = 'rpc/accept_invite'
  return fetch(buildUrl(endpoint), {
    method: 'POST',
    headers: buildFetchHeaders(),
    body: JSON.stringify({ invite_token: token }),
  }).then(async (response) => {
    if (!response.ok) {
      const errorBody = (await response.json()) as PostgREST_error
      if (isInviteInvalidError(errorBody)) {
        throw new Error(INVITE_INVALID_ERROR_MESSAGE)
      }
      throw new Error(`Failed to accept invite: ${response.status} ${response.statusText}`)
    }
  })
}

/**
 * Fetches the invites associated with a user's email.
 * 
 * @param email the email address of the user whose invites we want to fetch. This 
 * should be the email address associated with the user's account, and is used by 
 * the backend to find any invites that were sent to that email.
 * 
 * @returns a promise that resolves to an array of invite objects associated with 
 * the user's email. If no invites are found, it resolves to an empty array. If 
 * there is an error during the fetch, it rejects with an error message.
 * 
 * @throws will throw an error if there was a problem fetching the invites, with a 
 * message indicating the failure.
 */
export async function fetchUserInvitesByUserEmail(email: string): Promise<Invite_DTO[]> {
  const endpoint: ValidEndpoints = 'user_invites'
  const query = { email }

  return await fetchCollection<Invite_DTO>(endpoint, query).then((invites) => {
    if (!invites) {
      throw new Error('Failed to fetch user invites')
    }
    return invites
  })
}

/**
 * Fetches the invites associated with a specific fleet.
 * 
 * @param fleet_id the ID of the fleet for which to fetch invites.
 * 
 * @returns a promise that resolves to an array of invite objects associated with 
 * the fleet. If no invites are found, it resolves to an empty array. If there is 
 * an error during the fetch, it rejects with an error message.
 * 
 * @throws will throw an error if there was a problem fetching the invites, with a 
 * message indicating the failure.
 */
export async function fetchFleetInvitesByFleetId(fleet_id: string): Promise<Invite_DTO[]> {
  const endpoint: ValidEndpoints = 'user_invites'
  const query = { fleet_id }

  return await fetchCollection<Invite_DTO>(endpoint, query).then((invites) => {
    if (!invites) {
      throw new Error('Failed to fetch fleet invites')
    }
    return invites
  })
}
